import { useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';

import { JobsContext } from '../../context/JobsContext';
import {
  sendReminder,
  moveCandidateToPreviousStage,
  rejectCandidate,
  changeJoiningStatusToNoShow,
  sendOfferReminder,
} from '../../helpers/candidates';
import { useUiContext } from '../../context/UiContext';
import AdminService from '../../services/AdminService';
import { useUserStore } from '../../store';
import { CandidatesContext } from '../../context/CandidatesContext';
import { hasAuthData } from '../../helpers/authStorage';
import UserService from '../../services/UserService';

const isSendReminderOptionDisabled = (candidate) => {
  if (candidate?.status === 'Onboard' && candidate?.subStatus === 'Pending') {
    return true;
  }

  if (candidate?.status === 'Screen' && candidate?.subStatus === 'Completed') {
    return true;
  }

  if (candidate?.status === 'Interview' && candidate?.subStatus === 'Pending') {
    return true;
  }

  if (
    candidate?.status === 'ClientInterview' &&
    candidate?.subStatus === 'Pending'
  ) {
    return true;
  }

  if (
    candidate?.status === 'ClientSubmit' &&
    candidate?.subStatus === 'ClientNotified'
  ) {
    return true;
  }

  return false;
};

export default function useOptions({
  jobApplication,
  refetch,
  setIsOptionsListVisible,
  showAssessmentModal,
  changeJobApplicationStatus,
  interview,
  editCandidate,
  candidateId,
}) {
  const { t } = useTranslation();
  const screeningAvailable = useUserStore((state) => state.screeningAvailable);
  const isClient = useUserStore((state) => state.isClient);
  const isInterviewer = useUserStore((state) => state.isInterviewer);

  const isAuthorized = hasAuthData();

  const jobStatus = jobApplication?.jobPost?.status;

  const finalCandidateId = candidateId || jobApplication?.user?.id;

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
    enabled: isAuthorized,
  });

  const { data: candidate, refetch: refetchCandidate } = useQuery({
    queryKey: ['candidate', finalCandidateId],
    queryFn: () => AdminService.getCandidate({ id: finalCandidateId }),
    enabled: isAuthorized && !!finalCandidateId,
  });

  console.log('options candidate', candidate);
  console.log('options jobApplication', jobApplication);

  const activeInteview = interview;

  // const interviewFeedbacks =
  //   activeInteview?.jobApplicationInterviewFeedbacks?.find(
  //     (fb) => fb.feedbackType === 'Client'
  //   );
  // const hasInterviewFeedbacks = interviewFeedbacks?.length > 0;

  const externalInterviewerId = activeInteview?.externalInterviewers?.find(
    (interviewer) => interviewer.userId === me?.id
  )?.id;

  const companyManagerId = activeInteview?.companyManagers?.find(
    (manager) => manager.userId === me?.id
  )?.id;

  const internalUserId = activeInteview?.users?.find(
    (usr) => usr.id === me?.id
  )?.id;

  const clientInvitation = activeInteview?.jobApplicationInterviewInvites?.find(
    (invite) =>
      invite.externalInterviewerId === externalInterviewerId ||
      invite.companyManagerId === companyManagerId ||
      invite.userId === internalUserId
  );

  const hasPedningInvitation = clientInvitation?.status === 'Pending';

  const activeInteviewClient = jobApplication?.jobApplicationInterviews?.find(
    (intv) => intv.contactType === 'Client' && intv.status !== 'Canceled'
  );

  const isPastInterviewDateClient =
    new Date(activeInteviewClient?.interviewDate) < new Date();

  const {
    showSetOnboardingModal,
    showScheduleInterviewModal,
    showSetJoiningModal,
    showOfferModal,
    showRejectCandidateWithReasonModal,
    showChangeStageWithExceptionModal,
    showNextStepsModal,
    showInterviewSlotModal,
    showShareModal,
    showReportCandidateModal,
  } = useContext(JobsContext);
  const {
    showSubmitCandidateModal,
    showRejectCandidateModal,
    showInviteToJobModal,
    showAddCandidateNoteModal,
  } = useContext(CandidatesContext);
  const { showModal, showNotification, setIsFetching, showUnknownErrorModal } =
    useUiContext();
  const hasOnboarding = jobApplication?.jobApplicationOnboardings?.length;
  const hasJoining = jobApplication?.jobApplicationJoinings?.length;
  const hasAssessment = !!jobApplication?.assignedAssessments?.length;
  const isExternal = jobApplication?.jobPost?.jobPostType === 'External';

  const hideOptionsOnClick = (event, action) => {
    event.nativeEvent?.stopImmediatePropagation();
    event.stopPropagation();
    if (action) {
      action();
    }
    if (setIsOptionsListVisible) {
      setIsOptionsListVisible(false);
    }
  };

  const requestNoShowReject = () => {
    showModal({
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateModalTitle'
      ),
      text: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateModalText'
      ),
      onConfirm: () =>
        rejectCandidate(
          jobApplication,
          refetch,
          showNotification,
          true,
          setIsFetching
        ),
      dismissButtonLabel: t('common.no'),
    });
  };

  const requestNoShowJoinginReject = () => {
    showModal({
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateModalTitle'
      ),
      text: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateModalText'
      ),
      onConfirm: () =>
        changeJoiningStatusToNoShow(jobApplication, refetch, setIsFetching),
      dismissButtonLabel: t('common.no'),
    });
  };

  const setJobApplicationStatus = async (jobApplicationId, status) => {
    try {
      setIsFetching(true);

      await AdminService.changeMyJobApplicationStatus({
        jobApplicationId,
        status: status.status,
        subStatus: status.subStatus,
      });
      await refetch();
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  const declineEvent = async () => {
    const prevStatus = clientInvitation?.status;

    const undo = async () => {
      try {
        setIsFetching(true);
        await AdminService.changeJobApplicationInterviewInvite({
          interviewId: interview?.id,
          jobApplicationId: jobApplication?.id,
          invitationid: clientInvitation?.id,
          note: clientInvitation?.note,
          status: prevStatus,
        });
        if (refetch) {
          await refetch();
        }
      } catch (error) {
        showUnknownErrorModal();
      } finally {
        setIsFetching(false);
      }
    };

    showModal({
      icon: 'delete',
      title: t('pages.InterviewDashboard.declineInvitation'),
      text: t('pages.InterviewDashboard.areYouSureYouWantToDecline'),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.changeJobApplicationInterviewInvite({
            interviewId: interview?.id,
            jobApplicationId: jobApplication?.id,
            invitationid: clientInvitation?.id,
            status: 'Declined',
          });

          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Calendar.EventDetails.Interview.Interview invitation declined'
            ),
            undo,
          });
        } catch (error) {
          showUnknownErrorModal();
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const acceptEvent = async () => {
    setIsFetching(true);
    try {
      await AdminService.changeJobApplicationInterviewInvite({
        interviewId: interview?.id,
        jobApplicationId: jobApplication?.id,
        invitationid: clientInvitation?.id,
        note: clientInvitation?.note,
        status: 'Accepted',
      });
      if (refetch) {
        await refetch();
      }
      showNotification({
        text: t(
          'dashboardComponents.Calendar.EventDetails.Interview.Interview invitation accepted'
        ),
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }

    setIsFetching(false);
  };

  const cancelInterview = async () => {
    try {
      setIsFetching(true);

      await AdminService.cancelJobApplicationInterviewNew({
        jobApplicationId: jobApplication?.id,
        interviewId: activeInteview?.id,
      });
      await refetch();
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  const downloadCandidatePdf = async () => {
    try {
      setIsFetching(true);
      await AdminService.downloadCandidatePdf({
        id: jobApplication?.user?.id || candidateId,
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const rejectOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectTitle'
    ),
    hidden: isInterviewer || isClient,
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        showRejectCandidateWithReasonModal({
          candidate: jobApplication,
          refetch,
        });
      }),
  };

  const standardOptions = useMemo(
    () => [
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.submitAll'
        ),
        hidden: isClient || jobApplication?.id,
        onClick: (event) =>
          hideOptionsOnClick(event, () => {
            showSubmitCandidateModal({
              candidate: jobApplication?.user || candidate,
              refetch,
              jobPostId: jobApplication?.jobPostId,
            });
          }),
      },
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.inviteCandidateTitle'
        ),
        hidden: isClient || jobApplication?.id,
        onClick: (event) =>
          hideOptionsOnClick(event, () => {
            showInviteToJobModal(candidate);
          }),
      },
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.shareTitle'
        ),
        onClick: (event) =>
          hideOptionsOnClick(event, () =>
            showShareModal({ candidate: jobApplication?.user || candidate })
          ),
      },
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.addNoteTitle'
        ),
        hidden: isClient || isInterviewer,
        onClick: (event) =>
          hideOptionsOnClick(event, () => {
            showAddCandidateNoteModal(candidate, refetch);
          }),
      },
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.emailCandidateTitle'
        ),
        hidden: isClient,
        onClick: (event) =>
          hideOptionsOnClick(event, async () => {
            try {
              setIsFetching(true);
              // Prioritize jobApplication.user since it's synchronous and available immediately
              const jobAppUser = jobApplication?.user;

              // Try jobApplication.user first (synchronous, always available)
              let email =
                jobAppUser?.userProfile?.email ||
                jobAppUser?.UserProfile?.email ||
                jobAppUser?.email ||
                jobAppUser?.userName;

              // If no email found yet, try existing candidate data
              if (!email && candidate) {
                email =
                  candidate?.userProfile?.email ||
                  candidate?.UserProfile?.email ||
                  candidate?.email ||
                  candidate?.userName;
              }

              // If still no email and we have a candidate ID, refetch candidate data
              if (!email && finalCandidateId) {
                const { data: fetchedCandidate } = await refetchCandidate();

                if (fetchedCandidate) {
                  email =
                    fetchedCandidate?.userProfile?.email ||
                    fetchedCandidate?.UserProfile?.email ||
                    fetchedCandidate?.email ||
                    fetchedCandidate?.userName;
                }
              }

              if (!email) {
                return;
              }
              window.location.href = `mailto:${email}`;
            } catch (error) {
              console.error('Error in email click handler:', error);
            } finally {
              setIsFetching(false);
            }
          }),
      },
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.blockAndReportTitle'
        ),
        onClick: (event) =>
          hideOptionsOnClick(event, () =>
            showReportCandidateModal({
              candidate: jobApplication || candidate,
              refetch,
            })
          ),
      },
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.editCandidateTitle'
        ),
        hidden: isClient || isInterviewer || !editCandidate,
        onClick: (event) =>
          hideOptionsOnClick(event, () => {
            editCandidate();
          }),
      },
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.downloadPdfTitle'
        ),
        hidden: isClient,
        onClick: (event) =>
          hideOptionsOnClick(event, () => {
            downloadCandidatePdf();
          }),
      },
    ],
    [
      candidate,
      jobApplication,
      t,
      isClient,
      isInterviewer,
      refetch,
      editCandidate,
      downloadCandidatePdf,
      hideOptionsOnClick,
      showSubmitCandidateModal,
      showInviteToJobModal,
      showShareModal,
      showAddCandidateNoteModal,
      showReportCandidateModal,
      refetchCandidate,
      finalCandidateId,
    ]
  );

  const issueOfferOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.sendOfferTitle'
    ),
    hidden: isClient || isInterviewer,
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        showOfferModal({ candidate: jobApplication, refetch });
      }),
  };

  const assignAssessmentOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.assignAssessmentTitle'
    ),
    onClick: (event) =>
      hideOptionsOnClick(event, () =>
        showAssessmentModal({ jobApplication, refetch })
      ),
    hidden: !screeningAvailable,
  };

  let sendReminderOption = hasAssessment
    ? [
        {
          hidden: isSendReminderOptionDisabled(jobApplication) || isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.sendReminderTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () =>
              sendReminder(
                jobApplication,
                refetch,
                showNotification,
                setIsFetching
              )
            ),
        },
      ]
    : [assignAssessmentOption];

  if (
    jobApplication?.status === 'Offer' &&
    (jobApplication?.subStatus === 'Offered' ||
      jobApplication?.subStatus === 'Countered')
  ) {
    sendReminderOption = [
      {
        title: t(
          'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.sendReminderTitle'
        ),
        hidden: isClient,
        onClick: (event) =>
          hideOptionsOnClick(event, () =>
            sendOfferReminder(
              jobApplication,
              refetch,
              showNotification,
              setIsFetching
            )
          ),
      },
    ];
  }

  const confirmRejectionOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.confirmRejectionTitle'
    ),
    hidden: isClient || isInterviewer,
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        if (jobStatus === 'UnPublished') {
          return;
        }
        changeJobApplicationStatus(
          jobApplication.id,
          { status: jobApplication?.status, subStatus: 'Rejected' },
          () =>
            moveCandidateToPreviousStage(
              jobApplication?.status,
              jobApplication?.subStatus,
              jobApplication,
              refetch,
              setIsFetching
            )
        );
      }),
  };

  const rescintOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.restintTitle'
    ),
    hidden: !isClient,
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        if (jobStatus === 'UnPublished') {
          return;
        }
        changeJobApplicationStatus(jobApplication.id, {
          status: jobApplication?.prevStatus,
          subStatus: jobApplication?.prevSubStatus,
        });
      }),
  };

  const rescintInterViewOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.restintTitle'
    ),
    hidden: !isClient,
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        if (jobStatus === 'UnPublished') {
          return;
        }
        changeJobApplicationStatus(jobApplication.id, {
          status: 'ClientInterview',
          subStatus: 'Pending',
        });
      }),
  };

  const clientSubmitOptions = [
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.hireCandidateTitle'
      ),
      hidden: !isClient,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          if (jobStatus === 'UnPublished') {
            return;
          }
          changeJobApplicationStatus(
            jobApplication.id,
            { status: jobApplication?.status, subStatus: 'Hire' },
            () =>
              moveCandidateToPreviousStage(
                jobApplication?.status,
                jobApplication?.subStatus,
                jobApplication,
                refetch,
                setIsFetching
              )
          );
        }),
    },
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
      ),
      hidden: !isClient,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showRejectCandidateModal({ candidate: jobApplication, refetch });
        }),
    },
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.holdCandidateTitle'
      ),
      hidden: !isClient,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          if (jobStatus === 'UnPublished') {
            return;
          }
          changeJobApplicationStatus(
            jobApplication.id,
            { status: jobApplication.status, subStatus: 'Hold' },
            () =>
              moveCandidateToPreviousStage(
                jobApplication?.status,
                jobApplication?.subStatus,
                jobApplication,
                refetch,
                setIsFetching
              )
          );
        }),
    },
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.requestInterviewTitle'
      ),
      hidden: !isClient,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showInterviewSlotModal({ candidate: jobApplication, refetch });
        }),
    },
    ...standardOptions,
  ];

  const hireOptions = [
    rescintOption,
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectTitle'
      ),
      hidden: isClient || isInterviewer,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showRejectCandidateWithReasonModal({
            candidate: jobApplication,
            refetch,
          });
        }),
    },
    issueOfferOption,
    ...standardOptions,
  ];

  const holdOptions = [
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
      ),
      hidden: isClient || isInterviewer,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showModal({
            title: t(
              'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateWithTitle'
            ),
            text: t(
              'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateText'
            ),
            onConfirm: () =>
              rejectCandidate(
                jobApplication,
                refetch,
                showNotification,
                false,
                setIsFetching
              ),
            dismissButtonLabel: t('common.no'),
          });
        }),
    },
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.hireCandidateTitle'
      ),
      hidden: !isClient,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          if (jobStatus === 'UnPublished') {
            return;
          }
          changeJobApplicationStatus(
            jobApplication.id,
            { status: jobApplication?.status, subStatus: 'Hire' },
            () =>
              moveCandidateToPreviousStage(
                jobApplication?.status,
                jobApplication?.subStatus,
                jobApplication,
                refetch,
                setIsFetching
              )
          );
        }),
    },
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.requestInterviewTitle'
      ),
      hidden: !isClient,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showInterviewSlotModal({ candidate: jobApplication, refetch });
        }),
    },
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
      ),
      hidden: !isClient,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showRejectCandidateModal({ candidate: jobApplication, refetch });
        }),
    },
    ...standardOptions,
  ];

  const scheduleInterviewOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.scheduleInterviewTitle'
    ),
    hidden: isInterviewer || isClient,
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        showScheduleInterviewModal({ candidate: jobApplication, refetch });
      }),
  };

  const submitCanditateToClientOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.moveToClientSubmitTitle'
    ),
    hidden: !isExternal || isClient || isInterviewer,
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        if (jobStatus === 'UnPublished') {
          return;
        }
        showSubmitCandidateModal({
          candidate: jobApplication?.user,
          refetch,
          flow: 'client',
          jobPostId: jobApplication?.jobPostId,
        });
      }),
  };

  const newOptions = [
    scheduleInterviewOption,
    issueOfferOption,
    submitCanditateToClientOption,
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectTitle'
      ),
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showModal({
            title: t(
              'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateWithTitle'
            ),
            text: t(
              'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateText'
            ),
            onConfirm: () =>
              rejectCandidate(
                jobApplication,
                refetch,
                showNotification,
                false,
                setIsFetching
              ),
            dismissButtonLabel: t('common.no'),
          });
        }),
    },
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.assignAssessmentTitle'
      ),
      onClick: (event) =>
        hideOptionsOnClick(event, () =>
          showAssessmentModal({ jobApplication, refetch })
        ),
      hidden: !screeningAvailable,
    },
    ...standardOptions,
  ];

  const clienntInterviewAdvancedStagesOptions = [
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.moveToOfferTitle'
      ),
      hidden: isExternal,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          if (jobStatus === 'UnPublished') {
            return;
          }
          showChangeStageWithExceptionModal({
            candidate: jobApplication,
            refetch,
            stage: 'Offer',
          });
        }),
    },
    // {
    //   title: t(
    //     'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.passTitle'
    //   ),
    //   hidden: !isExternal,
    //   onClick: (event) =>
    //     hideOptionsOnClick(event, () => {
    //       if (jobStatus === 'UnPublished') {
    //         return;
    //       }
    //       changeJobApplicationStatus(
    //         jobApplication.id,
    //         { status: 'ClientInterview', subStatus: 'Passed' },
    //         () =>
    //           moveCandidateToPreviousStage(
    //             jobApplication?.status,
    //             jobApplication?.subStatus,
    //             jobApplication,
    //             refetch,
    //             setIsFetching
    //           )
    //       );
    //     }),
    // },
    rejectOption,
    ...sendReminderOption,
    ...standardOptions,
  ];

  const pendingOfferOptions = [
    {
      title: t(
        'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.issueOfferTitle'
      ),
      hidden: isClient || isInterviewer,
      onClick: (event) =>
        hideOptionsOnClick(event, () => {
          showOfferModal({ candidate: jobApplication, refetch });
        }),
    },
    rejectOption,
    ...(screeningAvailable ? [assignAssessmentOption] : []),
    ...standardOptions,
  ];

  const viewOfferOption = {
    hidden: !jobApplication?.jobApplicationOffers?.length || isInterviewer,
    title:
      jobApplication?.subStatus === 'Countered'
        ? t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.counterOfferTitle'
          )
        : t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.viewOfferTitle'
          ),
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        const readOnly =
          jobApplication?.subStatus !== 'Offered' &&
          jobApplication?.subStatus !== 'Countered';
        showOfferModal({ candidate: jobApplication, refetch, readOnly });
      }),
  };

  const reinstateNewOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.reinstateTitle'
    ),
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        if (jobStatus === 'UnPublished') {
          return;
        }
        changeJobApplicationStatus(
          jobApplication.id,
          { status: 'New', subStatus: 'Pending' },
          () =>
            moveCandidateToPreviousStage(
              jobApplication?.status,
              jobApplication?.subStatus,
              jobApplication,
              refetch,
              setIsFetching
            )
        );
      }),
  };

  const reinstateInterviewOption = {
    title: t(
      'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.reinstateTitle'
    ),
    onClick: (event) =>
      hideOptionsOnClick(event, () => {
        if (jobStatus === 'UnPublished') {
          return;
        }
        changeJobApplicationStatus(
          jobApplication.id,
          { status: 'Interview', subStatus: 'InProgress' },
          () =>
            moveCandidateToPreviousStage(
              jobApplication?.status,
              jobApplication?.subStatus,
              jobApplication,
              refetch,
              setIsFetching
            )
        );
      }),
  };

  const options = {
    Invited: {
      Pending: [rejectOption, ...standardOptions],
      Rejected: standardOptions,
      Withdrawn: [],
      Declined: [],
    },
    New: {
      Pending: newOptions,
      Rejected: [reinstateNewOption, ...standardOptions],
      Accepted: newOptions,
      Withdrawn: standardOptions,
    },
    Screen: {
      Requested: [rejectOption, ...sendReminderOption, ...standardOptions],
      Passed: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.moveToInterviewTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              changeJobApplicationStatus(
                jobApplication.id,
                { status: 'Interview', subStatus: 'Pending' },
                () =>
                  moveCandidateToPreviousStage(
                    jobApplication?.status,
                    jobApplication?.subStatus,
                    jobApplication,
                    refetch,
                    setIsFetching
                  )
              );
            }),
        },
        ...sendReminderOption,
        ...standardOptions,
      ],
      Completed: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.moveToInterviewTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              changeJobApplicationStatus(
                jobApplication.id,
                { status: 'Interview', subStatus: 'Pending' },
                () =>
                  moveCandidateToPreviousStage(
                    jobApplication?.status,
                    jobApplication?.subStatus,
                    jobApplication,
                    refetch,
                    setIsFetching
                  )
              );
            }),
        },
        rejectOption,
        ...sendReminderOption,
        ...standardOptions,
      ],
      Rejected: [
        // {
        //   title: 'Re-take Assessment',
        //   onClick: (event) =>
        //     hideOptionsOnClick(event, () => {
        //       showModal({
        //         title: 'Re-take Assessment',
        //         text: 'Are you sure you want the candidate to take the assessment again?',
        //         onConfirm: () =>
        //           retakeAssessment(
        //             candidate,
        //             refetch,
        //             showNotification,
        //             setIsFetching
        //           ),
        //         dismissButtonLabel: 'No',
        //       });
        //     }),
        // },
        ...standardOptions,
      ],
      Incomplete: [
        {
          title: 'Re-take Assessment',
          onClick: (event) =>
            hideOptionsOnClick(event, () =>
              showAssessmentModal({ jobApplication, refetch, retake: true })
            ),
        },
        ...standardOptions,
      ],
      Failed: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.reTakeAssessmentTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () =>
              showAssessmentModal({ jobApplication, refetch, retake: true })
            ),
        },
        ...standardOptions,
      ],
      Retake: [...sendReminderOption, ...standardOptions],
      Withdrawn: standardOptions,
    },
    Interview: {
      JobClosed: standardOptions,
      Pending: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      InProgress: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      Scheduled: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      Rescheduled: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      ChangeRequested: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      Accepted: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      Declined: standardOptions,
      Fail: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      Rejected: [reinstateInterviewOption, ...standardOptions],
      Canceled: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      Passed: [
        scheduleInterviewOption,
        issueOfferOption,
        submitCanditateToClientOption,
        rejectOption,
        ...standardOptions,
      ],
      Withdrawn: standardOptions,
    },
    ClientSubmit: {
      ClientNotified: clientSubmitOptions,
      ClientViewed: clientSubmitOptions,
      ClientNeedsInfo: clientSubmitOptions,
      PendingFeedback: clientSubmitOptions,
      Rejected: standardOptions,
      Accepted: standardOptions,
      JobClosed: standardOptions,
      Hire: hireOptions,
      Hold: holdOptions,
      ClientRejected: [
        rescintOption,
        confirmRejectionOption,
        ...standardOptions,
      ],
    },
    ClientInterview: {
      Hire: hireOptions,
      ClientRejected: [
        rescintOption,
        confirmRejectionOption,
        ...standardOptions,
      ],
      Hold: holdOptions,
      JobClosed: standardOptions,
      Requested: [
        {
          hidden: !isExternal || isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.scheduleInterviewTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showScheduleInterviewModal({
                candidate: jobApplication,
                refetch,
                editableInterviewProp:
                  jobApplication?.jobApplicationInterviews?.find(
                    (intv) =>
                      intv.contactType === 'Client' &&
                      intv.status === 'Requested'
                  ),
                initialContactType: 'Client',
              });
            }),
        },
        issueOfferOption,
        rejectOption,
        rescintInterViewOption,
        ...standardOptions,
      ],
      Pending: [
        {
          hidden: !isExternal || isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.scheduleInterviewTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showScheduleInterviewModal({
                candidate: jobApplication,
                refetch,
                initialContactType: 'Client',
              });
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.requestInterviewTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showInterviewSlotModal({ candidate: jobApplication, refetch });
            }),
        },
        issueOfferOption,
        rejectOption,
        ...sendReminderOption,
        ...standardOptions,
      ],
      InProgress: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.acceptInterviewTitle'
          ),
          hidden:
            !isClient || !hasPedningInvitation || isPastInterviewDateClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              acceptEvent();
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.declineInterviewTitle'
          ),
          hidden:
            !isClient || !hasPedningInvitation || isPastInterviewDateClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              declineEvent();
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.scheduleInterviewTitle'
          ),
          hidden: isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showScheduleInterviewModal({
                candidate: jobApplication,
                refetch,
                initialContactType: 'Client',
              });
            }),
        },
        issueOfferOption,
        rejectOption,
        ...sendReminderOption,
        ...standardOptions,
      ],
      Scheduled: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelInterviewTitle'
          ),
          hidden: isClient,
          onClick: (event) => hideOptionsOnClick(event, cancelInterview),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.acceptInterviewTitle'
          ),
          hidden:
            !isClient || !hasPedningInvitation || isPastInterviewDateClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              acceptEvent();
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.declineInterviewTitle'
          ),
          hidden:
            !isClient || !hasPedningInvitation || isPastInterviewDateClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              declineEvent();
            }),
        },
        issueOfferOption,
        ...clienntInterviewAdvancedStagesOptions,
      ],
      Rescheduled: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelInterviewTitle'
          ),
          hidden: isClient,
          onClick: (event) => hideOptionsOnClick(event, cancelInterview),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.acceptInterviewTitle'
          ),
          hidden:
            !isClient || !hasPedningInvitation || isPastInterviewDateClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              acceptEvent();
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.declineInterviewTitle'
          ),
          hidden:
            !isClient || !hasPedningInvitation || isPastInterviewDateClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              declineEvent();
            }),
        },
        issueOfferOption,
        ...clienntInterviewAdvancedStagesOptions,
      ],
      Accepted: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelInterviewTitle'
          ),
          hidden: isClient || isPastInterviewDateClient,
          onClick: (event) => hideOptionsOnClick(event, cancelInterview),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.declineInterviewTitle'
          ),
          hidden:
            !isClient || !hasPedningInvitation || isPastInterviewDateClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              declineEvent();
            }),
        },
        issueOfferOption,
        ...clienntInterviewAdvancedStagesOptions,
      ],
      ChangeRequested: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelInterviewTitle'
          ),
          onClick: (event) => hideOptionsOnClick(event, cancelInterview),
        },
        issueOfferOption,
        ...clienntInterviewAdvancedStagesOptions,
      ],
      ClientInterviewBothAccepted: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelInterviewTitle'
          ),
          onClick: (event) => hideOptionsOnClick(event, cancelInterview),
        },
        issueOfferOption,
        ...clienntInterviewAdvancedStagesOptions,
      ],
      ClientInterviewJobSeekerAccepted: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelInterviewTitle'
          ),
          onClick: (event) => hideOptionsOnClick(event, cancelInterview),
        },
        issueOfferOption,
        ...clienntInterviewAdvancedStagesOptions,
      ],
      ClientInterviewClientAccepted: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelInterviewTitle'
          ),
          onClick: (event) => hideOptionsOnClick(event, cancelInterview),
        },
        issueOfferOption,
        ...clienntInterviewAdvancedStagesOptions,
      ],
      Declined: [issueOfferOption, ...standardOptions],
      Fail: [issueOfferOption, ...clienntInterviewAdvancedStagesOptions],
      Rejected: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.reinstateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              changeJobApplicationStatus(
                jobApplication.id,
                { status: 'ClientInterview', subStatus: 'Reinstated' },
                () =>
                  moveCandidateToPreviousStage(
                    jobApplication.status,
                    jobApplication.subStatus,
                    jobApplication,
                    refetch,
                    setIsFetching
                  )
              );
            }),
        },
        ...standardOptions,
      ],
      Canceled: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.scheduleInterviewTitle'
          ),
          hidden: isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showScheduleInterviewModal({
                candidate: jobApplication,
                refetch,
                initialContactType: 'Client',
              });
            }),
        },
        issueOfferOption,
        rejectOption,
        ...sendReminderOption,
        ...standardOptions,
      ],
      Reinstated: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.scheduleInterviewTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showScheduleInterviewModal({
                candidate: jobApplication,
                refetch,
                initialContactType: 'Client',
              });
            }),
        },
        issueOfferOption,
        rejectOption,
        ...sendReminderOption,
        ...standardOptions,
      ],
      Passed: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.moveToOfferTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              changeJobApplicationStatus(
                jobApplication.id,
                { status: 'Offer', subStatus: 'Pending' },
                () =>
                  moveCandidateToPreviousStage(
                    jobApplication.status,
                    jobApplication.subStatus,
                    jobApplication,
                    refetch,
                    setIsFetching
                  )
              );
            }),
        },
        issueOfferOption,
        rejectOption,
        ...(screeningAvailable ? [assignAssessmentOption] : []),
        ...standardOptions,
      ],
    },
    Offer: {
      ClientRejected: [confirmRejectionOption, ...standardOptions],
      Pending: pendingOfferOptions,
      Rejected: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.reinstateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              changeJobApplicationStatus(
                jobApplication.id,
                { status: 'Offer', subStatus: 'Reinstated' },
                () =>
                  moveCandidateToPreviousStage(
                    jobApplication.status,
                    jobApplication.subStatus,
                    jobApplication,
                    refetch,
                    setIsFetching
                  )
              );
            }),
        },
        viewOfferOption,
        ...standardOptions,
      ],
      Declined: [viewOfferOption, standardOptions],
      Offered: [
        viewOfferOption,
        {
          hidden: isClient,
          title: 'Reject',
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateWithReasonModal({
                candidate: jobApplication,
                refetch,
              });
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        ...sendReminderOption,
        ...standardOptions,
      ],
      Countered: [
        viewOfferOption,
        rejectOption,
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        ...sendReminderOption,
        ...standardOptions,
      ],
      CounterAccepted: [
        viewOfferOption,
        rejectOption,
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        ...sendReminderOption,
        ...standardOptions,
      ],
      Accepted: [
        // {
        //   title: t(
        //     'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.moveToOnboardTitle'
        //   ),
        //   onClick: (event) =>
        //     hideOptionsOnClick(event, () => {
        //       if (jobStatus === 'UnPublished') {
        //         return;
        //       }
        //       changeJobApplicationStatus(
        //         jobApplication.id,
        //         { status: 'Onboard', subStatus: 'Pending' },
        //         () =>
        //           moveCandidateToPreviousStage(
        //             jobApplication?.status,
        //             jobApplication?.subStatus,
        //             jobApplication,
        //             refetch,
        //             setIsFetching
        //           )
        //       );
        //     }),
        // },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setStartDateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetJoiningModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        rejectOption,
        viewOfferOption,
        ...standardOptions,
      ],
      Withdrawn: standardOptions,
    },
    Onboard: {
      ClientRejected: [confirmRejectionOption, ...standardOptions],
      Pending: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setOnboardingTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetOnboardingModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        rejectOption,
        ...sendReminderOption,
        viewOfferOption,
        ...standardOptions,
      ],
      Scheduled: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.changeOnboardingTitle'
          ),
          hidden: isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetOnboardingModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.onboardedTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              setJobApplicationStatus(jobApplication.id, {
                status: 'Onboard',
                subStatus: 'Onboarded',
              });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setStartDateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetJoiningModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.noShowTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              requestNoShowReject();
            }),
        },
        rejectOption,
        /*  {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelOnboardingTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, async () => {
              try {
                setIsFetching(true);
                await AdminService.cancelJobApplicationOnboarding({
                  jobApplicationId: candidate.id,
                  onboardingId: candidate.jobApplicationOnboardings[0].id,
                });
              } catch (error) {
                console.log(error);
              } finally {
                await refetch();
                setIsFetching(false);
              }
            }),
        }, */
        ...sendReminderOption,
        viewOfferOption,
        ...standardOptions,
      ],
      Rescheduled: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.changeOnboardingTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetOnboardingModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.onboardedTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              setJobApplicationStatus(jobApplication.id, {
                status: 'Onboard',
                subStatus: 'Onboarded',
              });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setStartDateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetJoiningModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.noShowTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              requestNoShowReject();
            }),
        },
        rejectOption,
        ...sendReminderOption,
        viewOfferOption,
        ...standardOptions,
      ],
      ChangeRequested: hasJoining
        ? [
            {
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.changeStartTitle'
              ),
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showSetJoiningModal({ candidate: jobApplication, refetch });
                }),
            },
            rejectOption,
            {
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
              ),
              hidden: !isClient,
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showRejectCandidateModal({
                    candidate: jobApplication,
                    refetch,
                  });
                }),
            },
            ...sendReminderOption,
            viewOfferOption,
            ...standardOptions,
          ]
        : [
            {
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rescheduleTitle'
              ),
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showSetOnboardingModal({
                    candidate: jobApplication,
                    refetch,
                  });
                }),
            },
            {
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
              ),
              hidden: !isClient,
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showRejectCandidateModal({
                    candidate: jobApplication,
                    refetch,
                  });
                }),
            },
            rejectOption,
            viewOfferOption,
            ...sendReminderOption,
            ...standardOptions,
          ],

      StartConfirmed: [
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.joinedTitle'
          ),
          hidden: isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              showNextStepsModal({
                candidate: jobApplication,
                refetch,
              });
              /*  setJobApplicationStatus(candidate.id, {
                status: 'Onboard',
                subStatus: 'Joined',
              }); */
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.noShowTitle'
          ),
          hidden: isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              requestNoShowJoinginReject();
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        rejectOption,
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelStartDateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, async () => {
              try {
                setIsFetching(true);
                await AdminService.cancelStartDate({
                  jobApplicationId: jobApplication.id,
                  joiningId: jobApplication.jobApplicationJoinings?.at(-1)?.id,
                });
                await refetch();
              } catch (error) {
                console.log(error);
              } finally {
                setIsFetching(false);
              }
            }),
        },
        ...sendReminderOption,
        viewOfferOption,
        ...standardOptions,
      ],
      Accepted: [
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.onboardedTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              setJobApplicationStatus(jobApplication.id, {
                status: 'Onboard',
                subStatus: 'Onboarded',
              });
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setStartDateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetJoiningModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.noShowTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              requestNoShowReject();
            }),
        },
        rejectOption,
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rescheduleTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetOnboardingModal({ candidate: jobApplication, refetch });
            }),
        },
        //  ...sendReminderOption,
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.assignAssessmentTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () =>
              showAssessmentModal({ candidate: jobApplication, refetch })
            ),
          hidden: !screeningAvailable || isClient,
        },
        {
          hidden: isClient,
          // hidden: isSendReminderOptionDisabled(candidate),
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.sendReminderTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () =>
              sendReminder(
                jobApplication,
                refetch,
                showNotification,
                setIsFetching
              )
            ),
        },
        viewOfferOption,
        ...standardOptions,
      ],
      Onboarded: [
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setStartDateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showSetJoiningModal({ candidate: jobApplication, refetch });
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.noShowTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              requestNoShowJoinginReject();
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        rejectOption,
        ...sendReminderOption,
        viewOfferOption,
        ...standardOptions,
      ],
      StartDateSet: [
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.joinedTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              showNextStepsModal({
                candidate: jobApplication,
                refetch,
              });
              /*  setJobApplicationStatus(candidate.id, {
                status: 'Onboard',
                subStatus: 'Joined',
              }); */
            }),
        },
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.noShowTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              requestNoShowJoinginReject();
            }),
        },
        {
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
          ),
          hidden: !isClient,
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              showRejectCandidateModal({ candidate: jobApplication, refetch });
            }),
        },
        rejectOption,
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.cancelStartDateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, async () => {
              try {
                setIsFetching(true);
                await AdminService.cancelStartDate({
                  jobApplicationId: jobApplication.id,
                  joiningId: jobApplication.jobApplicationJoinings?.at(-1)?.id,
                });
                await refetch();
              } catch (error) {
                console.log(error);
              } finally {
                setIsFetching(false);
              }
            }),
        },
        ...sendReminderOption,
        viewOfferOption,
        ...standardOptions,
      ],
      NoShow: [viewOfferOption, standardOptions],
      Rejected: [
        {
          hidden: isClient,
          title: t(
            'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.reinstateTitle'
          ),
          onClick: (event) =>
            hideOptionsOnClick(event, () => {
              if (jobStatus === 'UnPublished') {
                return;
              }
              changeJobApplicationStatus(
                jobApplication.id,
                { status: 'Offer', subStatus: 'Reinstated' },
                () =>
                  moveCandidateToPreviousStage(
                    jobApplication.status,
                    jobApplication.subStatus,
                    jobApplication,
                    refetch,
                    setIsFetching
                  )
              );
            }),
        },
        ...standardOptions,
      ],
      Declined: [viewOfferOption, standardOptions],
      StartDeclined: [viewOfferOption, standardOptions],
      Canceled: hasOnboarding
        ? [
            {
              hidden: isClient,
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setStartDateTitle'
              ),
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showSetJoiningModal({ candidate: jobApplication, refetch });
                }),
            },
            {
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
              ),
              hidden: !isClient,
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showRejectCandidateModal({
                    candidate: jobApplication,
                    refetch,
                  });
                }),
            },
            rejectOption,
            ...sendReminderOption,
            viewOfferOption,
            ...standardOptions,
          ]
        : [
            {
              hidden: isClient,
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.setOnboardingTitle'
              ),
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showSetOnboardingModal({
                    candidate: jobApplication,
                    refetch,
                  });
                }),
            },
            {
              title: t(
                'dashboardComponents.Jobs.MyJobs.JobDetails.GridView.CandidatesCol.CandidateCard.CandidateMenu.rejectCandidateTitle'
              ),
              hidden: !isClient,
              onClick: (event) =>
                hideOptionsOnClick(event, () => {
                  showRejectCandidateModal({
                    candidate: jobApplication,
                    refetch,
                  });
                }),
            },
            rejectOption,
            ...sendReminderOption,
            viewOfferOption,
            ...standardOptions,
          ],
      Joined: [standardOptions],
      Withdrawn: [viewOfferOption, standardOptions],
    },
  };

  return {
    standardOptions,
    options,
    isOnlyStandardOptions: (optionsToShow) =>
      optionsToShow?.every((option) =>
        standardOptions.some((sOpt) => option.title === sOpt.title)
      ),
  };
}
